<template>
  <div>
    <v-row class="align-center ml-0 pt-2 pb-2">
      <v-col cols="3" sm="3">
        <h3>{{ $t("template.lblTemplateType") }}</h3>
      </v-col>
      <v-col cols="4" sm="4">
        <v-select
          v-model="selectedTemplateTypeId"
          :items="emailTemplateTypes"
          filled
          single-line
          hide-details=""
          item-text="name"
          item-value="id"
          dense
          :menu-props="{ bottom: true, offsetY: true }"
        ></v-select>
      </v-col>
      <v-col cols="5" sm="5" class="text-right">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <a-btn
              class="mr-4"
              color="primaryDark"
              dark
              v-bind="attrs"
              v-on="on"
              @click="onCreateTemplate"
              ><v-icon size="16">mdi-plus</v-icon
              >{{ $t("template.lblTemplate") }}
            </a-btn>
          </template>
          <span>{{ $t("template.lblCreateTemplate") }}</span>
        </v-tooltip>
      </v-col>
    </v-row>
    <v-col cols="12" sm="12">
      <hr />
    </v-col>
    <v-col cols="12" sm="12">
      <template-table
        :setting="setting"
        :items="templateItems"
        @edit="onEdit"
        @copy="onCopy"
        @delete="onDelete"
        @onTemplateChange="onTemplateChange"
      ></template-table>
    </v-col>
    <confirm-dialog ref="confirm"></confirm-dialog>
    <email-template-editor
      v-if="selectedTemplateForm"
      v-model="showEmailTemplateEditor"
      :item="selectedTemplateForm"
      :templateItems="templateItems"
      @success="onSuccess"
    ></email-template-editor>
    <message-sheet
      :showMessage="displayMessage"
      :message="message"
      :messageType="messageType"
      @close="displayMessage = false"
    ></message-sheet>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import EmailTemplateEditor from "./EmailTemplateEditor.vue";
import TemplateTable from "./TemplateTable.vue";
import { createUUID } from "../../utils/util";
import { cloneDeep } from "lodash";
import ConfirmDialog from "../ConfirmDialog.vue";
import MessageSheet from "../MessageSheet.vue";

export default {
  components: {
    TemplateTable,
    EmailTemplateEditor,
    ConfirmDialog,
    MessageSheet,
  },
  name: "EmailNotificationTemplate",
  props: {
    setting: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      displayMessage: false,
      messageType: "",
      message: "",
      refTemplate: {
        id: null,
        subject: "",
        message: "",
        templateTypeId: "",
        active: false,
      },
      showEmailTemplateEditor: false,
      selectedTemplateTypeId: null,
      selectedTemplateForm: null,
      templateItems: [],
      currentSelectedTemplatedType: null,
    };
  },
  computed: {
    ...mapGetters({
      masterSettingTypes: "tenant/masterSettingTypes",
      tenantSettingConfig: "tenant/tenantSettingConfig",
    }),
    defaultEmailTemplates() {
      const defaultTemplate = this.masterSettingTypes(
        "default_email_templates"
      );
      return defaultTemplate;
    },
    emailTemplates() {
      return this.tenantSettingConfig("email_templates")?.email_templates || [];
    },
    emailTemplateTypes() {
      const items = this.masterSettingTypes("email_template_names");
      return items.map((item) => {
        return {
          id: item.id,
          name: this.$t(`template.${item.key}`),
        };
      });
    },
    selectedLang() {
      return this.$i18n.locale;
    },
    subjectList(){
      const sselectedId = this.selectedTemplateForm && this.selectedTemplateForm.id ? this.selectedTemplateForm.id : null
      const subjects = this.templateItems
          .filter(template => template.id !== sselectedId)
          .map(template => template.subject);
      return subjects
    }
  },
  mounted() {
    // Set default to the first item in the list
    if (this.emailTemplateTypes.length > 0) {
      this.selectedTemplateTypeId = this.emailTemplateTypes[0].id; // Ensure it matches item-value type
    }
  },
  watch: {
    selectedTemplateTypeId(value) {
      this.templateItems = this.getTemplateByType(value);
    },
    showEmailTemplateEditor(value) {
      if (!value) {
        this.selectedTemplateForm = false;
      }
    },
    selectedLang(value) {
      this.templateItems = this.getTemplateByType(
        this.currentSelectedTemplatedType
      );
    },
  },
  methods: {
    ...mapActions({
      deleteEmailTemplate: "tenant/deleteEmailTemplate",
      updateEmailTemplate: "tenant/updateEmailTemplate",
    }),
    onCreateTemplate,
    getTemplateByType,
    onEdit,
    onCopy,
    onSuccess,
    onDelete,
    onTemplateChange,
    showMessageSheet,
  },
};

async function onTemplateChange(item) {
  let loader;
  try {
    loader = this.$localLoader.show();
    const templateData = {
      configname: "email_templates",
      template_type_id: this.selectedTemplateTypeId,
      email_template: item,
    };
    await this.updateEmailTemplate(templateData);
    this.$localLoader.hide(loader);
    this.showMessageSheet(this.$t("template.lblTemplateSelection"), "success");
  } catch (error) {
    this.$localLoader.hide(loader);
  }
}

function onSuccess(params) {
  let loader;
  try {
    loader = this.$localLoader.show();
    const { isSuccess, isUpdate } = params;
    if (isSuccess) {
      this.showMessageSheet(
        `${
          isUpdate
            ? this.$t("template.lblTemplateUpdateSuccessfully")
            : this.$t("template.lblTemplateAddedSuccessfully")
        }`,
        "success"
      );
    }
    this.templateItems = this.getTemplateByType(this.selectedTemplateTypeId);
    this.selectedTemplateForm = false;
    this.$localLoader.hide(loader);
  } catch (error) {
    this.$localLoader.hide(loader);
  }
}

async function onDelete(id) {
  let loader;
  try {
    if (
      await this.$refs.confirm.open(
        this.$t("agenda.hDeleteMessage"),
        this.$t("agenda.lblDeleteMessage")
      )
    ) {
      const params = {
        template_id: id,
        template_type_id: this.selectedTemplateTypeId,
      };
      loader = this.$localLoader.show();
      await this.deleteEmailTemplate(params);
      this.templateItems = this.getTemplateByType(this.selectedTemplateTypeId);
      this.$localLoader.hide(loader);
      this.showMessageSheet(this.$t("template.lblTemplateDelete"), "success");
    }
  } catch (error) {
    this.$localLoader.hide(loader);
  }
}

function onEdit(item) {
  this.showEmailTemplateEditor = true;
  let sForm = cloneDeep(this.refTemplate);
  sForm.id = item.id;
  sForm.templateTypeId = this.selectedTemplateTypeId;
  sForm.subject = item.subject;
  sForm.message = item.body;
  sForm.active = item.active;
  this.selectedTemplateForm = sForm;
  this.selectedTemplate = item;
}

function onCopy(item) {
  this.showEmailTemplateEditor = true;
  let sForm = cloneDeep(this.refTemplate);
  sForm.templateTypeId = this.selectedTemplateTypeId;
  sForm.subject = `${this.$t("poll.tipCopy")} - ${item.subject}`
  sForm.message = item.body;
  sForm.active = false;
  sForm.is_copy = true;
  this.selectedTemplateForm = sForm;
  this.selectedTemplate = item;
}

function getTemplateByType(parentKey) {
  this.currentSelectedTemplatedType = parentKey;
  let userTemplates = [];
  console.log(parentKey, 'parentKey');
  if (this.emailTemplates.hasOwnProperty(parentKey)) {
    // Return the values of the nested object as an array
    userTemplates = Object.values(this.emailTemplates[parentKey]);
  }

  const isAnyActive = userTemplates.some((t) => t.active === true);
  const data = this.defaultEmailTemplates.filter(
    (t) => t.template_type === parentKey && t.language === this.selectedLang
  );
  const defaultTemplate = data.map((obj) => {
    return { ...obj, default: true, active: !isAnyActive };
  });

  const templates = defaultTemplate.concat(userTemplates);
  return templates;
}

function onCreateTemplate() {
  let sForm = cloneDeep(this.refTemplate);
  sForm.id = null;
  sForm.templateTypeId = this.selectedTemplateTypeId;
  this.selectedTemplateForm = sForm;
  this.showEmailTemplateEditor = true;
}

function showMessageSheet(message, messageType) {
  this.displayMessage = true;
  this.message = message;
  this.messageType = messageType;
}
</script>

<style>
</style>